/**
 * KOA-18136 — WHICH components on a managed workflow task screen belong to the
 * platform, and WHICH argument of a tool call addresses them.
 *
 * Split out of `managed-task-screen-guard.ts` so the two questions the guard
 * asks are separately testable: "is this target the wizard's?" (here) and
 * "is this operation allowed on it?" (the guard).
 *
 * ## Why ownership is not one flag
 *
 * The wizard stamps what it generates (`WorkflowScreenService`
 * `CreateFromTemplate_WorkflowStepFormScreen`), and the stamp is deliberately
 * two-level — read from the backend, not inferred:
 *
 *   - `IsInteractionDisabled = true` on the Header, the Form, the Footer and the
 *     decision ButtonGroup → THAT element is the platform's.
 *   - `IsChildrenInteractionDisabled = true` on the decision ButtonGroup only →
 *     its whole subtree is the platform's.
 *
 * The Form gets the first flag but NOT the second. So the fields inside the
 * generated form carry no stamp of their own, and a stamp-only test would call
 * them free. They are not: `FormBuilderService.ApplyUserChangedPropertiesToFormDi`
 * re-imposes each field Label's text from the metadata field name and each field
 * cell Col's spans from the section layout on every `UpdateWorkflowScreens`.
 * Membership in the form subtree is therefore its own ownership signal, and the
 * `Form` element type is how it is recognised.
 *
 * That asymmetry is a feature, not a gap to patch: a container stamped
 * `IsInteractionDisabled` WITHOUT `IsChildrenInteractionDisabled` (Header,
 * Footer) protects itself while leaving its inside open — which is exactly the
 * room the refinement turn needs to add new components to a task screen without
 * touching anything the wizard put there.
 */

/** One action instance as it sits on a component's event. */
export interface IOwnershipActionNode {
  id?: string;
  /** if/else and for-each branches nest further action instances. */
  ifThenElseData?: { ifActions?: IOwnershipActionNode[]; elseActions?: IOwnershipActionNode[] };
  forEach?: IOwnershipActionNode[];
  recurringActionsData?: { actions?: IOwnershipActionNode[] };
}

/** One event on a component, with the action instances attached to it. */
export interface IOwnershipEventNode {
  eventName?: string;
  actions?: IOwnershipActionNode[];
}

/** Minimal shape of one display item this module reads. */
export interface IOwnershipDisplayItem {
  parentId?: number | null;
  item?: { name?: string; events?: IOwnershipEventNode[] };
  isInteractionDisabled?: boolean;
  isChildrenInteractionDisabled?: boolean;
}

/** Canvas dictionary keyed by numeric kuikaId, as the screen provider holds it. */
export type OwnershipDictionary = Record<number, IOwnershipDisplayItem>;

/**
 * Which platform-owned region a target sits in.
 *
 * The label drives the refusal message only — every region is equally off
 * limits for anything but styling. `form` and `buttons` have a durable DSL
 * alternative to name; `chrome` (a stamped Header/Footer) has none, because
 * nothing about it is authored through the workflow DSL.
 */
export type WizardOwnedRegion = "form" | "buttons" | "chrome";

/** Parent-chain hop ceiling — a corrupt cycle must never hang the dispatcher. */
const MAX_HOPS: number = 64;

/**
 * The platform-owned region containing `kuikaId`, or null when the target is
 * ordinary Designer territory (or not on this canvas at all).
 *
 * Walks the parent chain once and answers with the NEAREST meaningful owner, so
 * a decision Button inside a Header-hosted ButtonGroup reports `buttons` rather
 * than the Header's `chrome`.
 */
export function findWizardOwnedRegion(
  dict: OwnershipDictionary | undefined,
  kuikaId: string
): WizardOwnedRegion | null {
  if (dict === undefined) return null;
  const start: number = Number.parseInt(kuikaId, 10);
  if (!Number.isFinite(start)) return null;

  let current: number | null = start;
  let hops: number = 0;
  let isSelf: boolean = true;
  /** A stamp was seen, but no Form/ButtonGroup to name it more precisely. */
  let stamped: boolean = false;

  while (current !== null && hops < MAX_HOPS) {
    const di: IOwnershipDisplayItem | undefined = dict[current];
    if (di === undefined) return stamped ? "chrome" : null;

    const name: string = di.item?.name ?? "";
    if (name === "Form") return "form";
    if (name === "ButtonGroup") return "buttons";

    // The element itself is owned when stamped; an ANCESTOR only extends
    // ownership downward when it stamped its children too. A Header stamped
    // `isInteractionDisabled` alone protects itself, not what is added inside it.
    if (isSelf && di.isInteractionDisabled === true) stamped = true;
    if (di.isChildrenInteractionDisabled === true) stamped = true;

    current = typeof di.parentId === "number" ? di.parentId : null;
    hops += 1;
    isSelf = false;
  }
  return stamped ? "chrome" : null;
}

/**
 * KOA-18136 — DOES THIS CANVAS CARRY THE WIZARD'S STAMP AT ALL?
 *
 * A second, registry-independent answer to "is this a managed task screen".
 *
 * The guard's only source until now was `project.processStepScreenIds`, the
 * platform's screen→step registry — and that registry is measurably not
 * dependable: it answers during a build and is gone when a later pass asks
 * (the same loss already forced `list_process_binding_sources` to accept the
 * process id from its caller). Live `3e95f572`: a turn re-ran the Designer over
 * two EXISTING task screens and 57 renders, three visibility writes and a table
 * rewrite all landed, because the registry did not name them at write time —
 * while a server-side pass in the SAME turn recognised both and skipped them.
 * Two sources of truth, and the enforcing one was the unreliable one.
 *
 * The canvas carries its own evidence: the wizard stamps what it generates
 * (`IsInteractionDisabled` on Header / Form / Footer / decision ButtonGroup,
 * `IsChildrenInteractionDisabled` on the ButtonGroup), read from the backend
 * and never inferred. A stamp on this canvas means the platform generated part
 * of it, which is exactly the fact the guard needs.
 *
 * Deliberately a WEAK, additive signal: it only ever ADDS managed-ness, never
 * removes it, so a screen the registry knows stays protected even with an
 * unreadable dictionary. And it says nothing about WHICH component is owned —
 * `findWizardOwnedRegion` still decides that per target, so an ordinary
 * component added to a managed screen is judged exactly as before.
 */
export function canvasCarriesWizardStamp(dict: OwnershipDictionary | undefined): boolean {
  if (dict === undefined) return false;
  for (const di of Object.values(dict)) {
    if (di?.isInteractionDisabled === true) return true;
    if (di?.isChildrenInteractionDisabled === true) return true;
  }
  return false;
}

/** Traversal ceilings — an oversized or self-referential payload must not stall.
 *  Shared by both deep scans below. */
const MAX_NODES: number = 4096;
const MAX_DEPTH: number = 16;

/**
 * Every component TYPE an operation's input asks to create, at any depth.
 *
 * Separate from the id scan because the risk it answers is the opposite shape:
 * a second decision ButtonGroup is dangerous precisely because it targets
 * ordinary Designer territory — the screen root — so the ownership walk never
 * sees it. What matters there is WHAT is being created, not WHERE.
 *
 * Same deep/JSON-string traversal as `collectTargetKuikaIds`, and for the same
 * reason: `ui_component.add` takes a `components[]` batch, the render expander
 * produces that batch from markup, and several handlers accept it as a JSON
 * STRING. A shape-by-shape reader would return nothing for a payload that does
 * create one.
 */
export function collectCreatedComponentNames(input: unknown): string[] {
  const out: string[] = [];
  const seen: Set<object> = new Set<object>();
  let budget: number = MAX_NODES;

  const visit = (node: unknown, depth: number): void => {
    if (budget <= 0 || depth > MAX_DEPTH) return;
    budget -= 1;

    if (typeof node === "string") {
      const trimmed: string = node.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          visit(JSON.parse(trimmed), depth + 1);
        } catch {
          // Not JSON — an ordinary string value.
        }
      }
      return;
    }

    if (Array.isArray(node)) {
      for (const entry of node) visit(entry, depth + 1);
      return;
    }

    if (node === null || typeof node !== "object") return;
    if (seen.has(node)) return;
    seen.add(node);

    for (const [key, value] of Object.entries(node)) {
      if (key === "componentName" || key === "componentType") {
        if (typeof value === "string" && value.length > 0) out.push(value);
        continue;
      }
      visit(value, depth + 1);
    }
  };

  visit(input, 0);
  return out;
}

/** Field names that carry a component id in any tool input, at any depth. */
const ID_FIELDS: readonly string[] = [
  "kuikaId",
  "kuikaIds",
  "rowKuikaId",
  "firstKuikaId",
  "secondKuikaId",
  "componentKuikaId",
  "targetKuikaId"
];

/**
 * Every component id an operation's input addresses, at any depth.
 *
 * Deliberately a DEEP scan rather than a list of known argument shapes. The
 * write surface is wide and irregular — `ui_component` takes `kuikaId` /
 * `kuikaIds` / `items[].kuikaId`, `ui_property` takes `bindings[].kuikaId` and
 * `datasource.kuikaId`, `ui_bind.apply` nests further — and several handlers
 * accept those arrays as JSON STRINGS (`safeParse` in `property-tools.ts`), so a
 * shape-by-shape reader silently returns nothing for a payload that does address
 * a protected component. Missing an id here means the ownership rule does not
 * apply to that call, which is the one failure mode this module must not have.
 *
 * A new argument shape is covered the moment it names its id `kuikaId`.
 */
export function collectTargetKuikaIds(input: unknown): string[] {
  const out: string[] = [];
  const seen: Set<object> = new Set<object>();
  let budget: number = MAX_NODES;

  const pushId = (value: unknown): void => {
    if (typeof value === "string" && value.length > 0) out.push(value);
    else if (typeof value === "number" && Number.isFinite(value)) out.push(String(value));
  };

  const visit = (node: unknown, depth: number): void => {
    if (budget <= 0 || depth > MAX_DEPTH) return;
    budget -= 1;

    // Handlers accept id-bearing arrays as JSON strings; parse opportunistically.
    if (typeof node === "string") {
      const trimmed: string = node.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          visit(JSON.parse(trimmed), depth + 1);
        } catch {
          // Not JSON — an ordinary string value, nothing to collect.
        }
      }
      return;
    }

    if (Array.isArray(node)) {
      for (const entry of node) visit(entry, depth + 1);
      return;
    }

    if (node === null || typeof node !== "object") return;
    if (seen.has(node)) return;
    seen.add(node);

    for (const [key, value] of Object.entries(node)) {
      if (ID_FIELDS.includes(key)) {
        if (Array.isArray(value)) for (const entry of value) pushId(entry);
        else pushId(value);
        continue;
      }
      visit(value, depth + 1);
    }
  };

  visit(input, 0);
  return out;
}

/**
 * KOA-18136 — AN ACTION IS PART OF THE COMPONENT IT SITS ON.
 *
 * The user's rule, stated in their own words and not narrowed since: *"o bileşene
 * dair stil haricinde hiçbir şeye dokunulamaz, o bileşene bağlı action ve onun
 * bindingleri de o bileşenin içinde, yani o bileşene dahil."*
 *
 * The gate used to ask the wrong question. It scanned the payload for a COMPONENT
 * id and, finding none, concluded the write addressed nothing protected. But a
 * decision button's `ResumeProcess` parameters are written through the ACTION
 * INSTANCE — `{ actionId, parameterName, … }` — and an action-instance id is not a
 * component id, so that write looked target-less and passed. Live session
 * 379e6653 came back with those parameters emptied on two task screens while the
 * screens were correctly registered and the gate was active: the shape of write
 * that did it was invisible to the shape of question being asked.
 *
 * So ownership is resolved from the ACTION's HOST. Every action instance on the
 * canvas hangs off some component's event; that component is what owns it, and if
 * that component is wizard-generated the action and its bindings are as untouchable
 * as the component itself. Nested branches count — an `IfThenElse` on a decision
 * button hosts its `if`/`else` actions on that same button.
 */

/** Field names that carry an ACTION INSTANCE id in any tool input, at any depth. */
const ACTION_ID_FIELDS: readonly string[] = ["actionId", "actionIds", "branchActionId", "parentActionId"];

/**
 * Every action-instance id an operation's input addresses, at any depth.
 *
 * Same deep + JSON-string traversal as the component scan, for the same reason:
 * `set_multiple_parameter_bindings` carries them inside `bindings[]`, and several
 * handlers accept that array as a JSON string.
 */
export function collectTargetActionIds(input: unknown): string[] {
  const out: string[] = [];
  const seen: Set<object> = new Set<object>();
  let budget: number = MAX_NODES;

  const pushId = (value: unknown): void => {
    if (typeof value === "string" && value.length > 0) out.push(value);
  };

  const visit = (node: unknown, depth: number): void => {
    if (budget <= 0 || depth > MAX_DEPTH) return;
    budget -= 1;

    if (typeof node === "string") {
      const trimmed: string = node.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          visit(JSON.parse(trimmed), depth + 1);
        } catch {
          // Not JSON — an ordinary string value.
        }
      }
      return;
    }
    if (Array.isArray(node)) {
      for (const entry of node) visit(entry, depth + 1);
      return;
    }
    if (node === null || typeof node !== "object") return;
    if (seen.has(node)) return;
    seen.add(node);

    for (const [key, value] of Object.entries(node)) {
      if (ACTION_ID_FIELDS.includes(key)) {
        if (Array.isArray(value)) for (const entry of value) pushId(entry);
        else pushId(value);
        continue;
      }
      visit(value, depth + 1);
    }
  };

  visit(input, 0);
  return out;
}

/**
 * Payload keys that carry the NAME of an action parameter being written.
 *
 * `name` is deliberately included even though it is a common key: this walker is
 * only consulted to decide whether a write TOUCHES a named parameter, and on that
 * question a false positive costs one refusal with a durable alternative, while a
 * false negative costs the link between a decision button and its interaction.
 */
const PARAMETER_NAME_FIELDS: readonly string[] = ["parameterName", "paramName", "name"];

/**
 * Every parameter NAME a payload addresses, lower-cased.
 *
 * Companion to {@link collectTargetActionIds}: that one answers "which action does
 * this write land on", this one answers "which of its parameters". Some writes carry
 * only the parameter name and an action id, others carry a nested list of parameters,
 * and a few arrive as a JSON string — the same walk covers all three.
 */
export function collectTargetParameterNames(input: unknown): string[] {
  const out: string[] = [];
  const seen: Set<object> = new Set<object>();
  let budget: number = MAX_NODES;

  const pushName = (value: unknown): void => {
    if (typeof value === "string" && value.trim().length > 0) out.push(value.trim().toLowerCase());
  };

  const visit = (node: unknown, depth: number): void => {
    if (budget <= 0 || depth > MAX_DEPTH) return;
    budget -= 1;

    if (typeof node === "string") {
      const trimmed: string = node.trim();
      if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
        try {
          visit(JSON.parse(trimmed), depth + 1);
        } catch {
          // Not JSON — an ordinary string value.
        }
      }
      return;
    }
    if (Array.isArray(node)) {
      for (const entry of node) visit(entry, depth + 1);
      return;
    }
    if (node === null || typeof node !== "object") return;
    if (seen.has(node)) return;
    seen.add(node);

    for (const [key, value] of Object.entries(node)) {
      if (PARAMETER_NAME_FIELDS.includes(key)) {
        if (Array.isArray(value)) for (const entry of value) pushName(entry);
        else pushName(value);
        continue;
      }
      visit(value, depth + 1);
    }
  };

  visit(input, 0);
  return out;
}

/** Depth ceiling for the nested-branch walk inside one event. */
const MAX_ACTION_DEPTH: number = 12;

function actionTreeHasId(actions: readonly IOwnershipActionNode[] | undefined, wanted: string, depth: number): boolean {
  if (actions === undefined || depth > MAX_ACTION_DEPTH) return false;
  for (const action of actions) {
    if (action === null || typeof action !== "object") continue;
    if (typeof action.id === "string" && action.id === wanted) return true;
    if (actionTreeHasId(action.ifThenElseData?.ifActions, wanted, depth + 1)) return true;
    if (actionTreeHasId(action.ifThenElseData?.elseActions, wanted, depth + 1)) return true;
    if (actionTreeHasId(action.forEach, wanted, depth + 1)) return true;
    if (actionTreeHasId(action.recurringActionsData?.actions, wanted, depth + 1)) return true;
  }
  return false;
}

/**
 * The kuikaId of the component hosting `actionId`, or null when no component on
 * this canvas carries it.
 *
 * Null is NOT "safe": on a managed screen an action whose host cannot be found is
 * exactly the write that must not be waved through, and the caller treats it that
 * way. Returning null rather than guessing keeps that decision at the gate.
 */
export function findActionHostKuikaId(dict: OwnershipDictionary | undefined, actionId: string): string | null {
  if (dict === undefined || actionId.length === 0) return null;
  for (const key of Object.keys(dict)) {
    const di: IOwnershipDisplayItem | undefined = dict[key as unknown as number];
    const events: readonly IOwnershipEventNode[] = di?.item?.events ?? [];
    for (const event of events) {
      if (actionTreeHasId(event.actions, actionId, 0)) return String(key);
    }
  }
  return null;
}
